import { useState, useEffect } from "react";
import { IoLocationSharp } from "react-icons/io5";
import { FaLongArrowAltRight, FaLongArrowAltLeft } from "react-icons/fa";
import axios from "axios";
import { useRouter } from "next/router";
import CardSkeleton from "@/components/common/cardSkeleton";
import { getCategoryName } from "../../../utils/apiRoute";
import Image from "next/image";
import SkeletonTest from "@/components/common/skeletonTest";
import Link from "next/link";
import { hostNameFunction } from "../../../utils/host-function";
import Head from "next/head";
import Layout from "@/components/common/Layout";
import COUNTRY_NAME from "../../locals/countryName.json";
import translations from "../../locals/countryTranslation.json";
import { getLocals } from "../../../getLocal";

export default function Category({
  fullObj,
  hostnameNew,
  countryName,
  countryTranslations,
  localizedVariable,
  translations,
  data,
}) {
  const length = data?.businessNames?.length || 0;
  const router = useRouter();
  const { categoryName } = router.query;
  const hostname = hostNameFunction();
  const [showPreviousButton, setShowPreviousButton] = useState(false);
  const [localizedStrings, setLocalizedStrings] = useState();
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchLocalizedStrings = async () => {
      const lang = hostname ? hostname : "en";
      const strings = await getLocals(lang);
      setLocalizedStrings(strings);
    };

    fetchLocalizedStrings();
  }, []);

  const [categoryData, setCategoryData] = useState({
    data: [],
    categoryNamePlural: "",
    businessCount: 0,
    businessNames: [],
    areaName: "",
    categoryNameNew: "",
  });
  let pageTitle = "";

  if (hostnameNew == "hi") {
    pageTitle = `${translations?.[0]} ${
      localizedVariable?.Index.categoryFirstLine4
    } ${data?.categoryNamePlural}- ${data?.businessNames?.[0]} ${
      localizedVariable?.Index.Title.categoryTitle
    } ${data?.businessNames?.[length - 1]}`;
  } else {
    pageTitle = `${data?.categoryNamePlural} ${
      localizedVariable?.Index.categoryFirstLine4
    } ${translations?.[0]}- ${data?.businessNames?.[0]} ${
      localizedVariable?.Index.Title.categoryTitle
    } ${data?.businessNames?.[length - 1]}`;
  }

  let metaDescription = "";
  let metaKeywords = "";
  let description = "";
  if (hostnameNew == "hi") {
    description = `${data.businessNames
      ?.map(
        (name) =>
          `${translations?.[0]} ${localizedVariable?.Index.categoryFirstLine4} ${name}`
      )
      .join(", ")}`;
  } else {
    description = `${data.businessNames
      ?.map(
        (name) =>
          `${name} ${localizedVariable?.Index.categoryFirstLine4} ${translations?.[0]}`
      )
      .join(", ")}`;
  }

  if (hostnameNew == "hi") {
    metaDescription = `${translations?.[0]} ${localizedVariable?.Index.categoryFirstLine4} ${localizedVariable?.Index.Category.name} ${data?.categoryNamePlural} - ${description}`;
  } else {
    metaDescription = `${localizedVariable?.Index.Category.name} ${data?.categoryNamePlural} ${localizedVariable?.Index.categoryFirstLine4} ${translations?.[0]}- ${description}`;
  }

  if (hostnameNew == "hi") {
    metaKeywords = `${translations?.[0]} ${localizedVariable?.Index.categoryFirstLine4} ${data?.categoryNamePlural}- ${description}`;
  } else {
    metaKeywords = `${data?.categoryNamePlural} ${localizedVariable?.Index.categoryFirstLine4} ${translations?.[0]}- ${description}`;
  }

  const [currentPage, setCurrentPage] = useState(1);
  const url = `/category/${router.query.categoryName}`;
  // Extract categorySlug and areaSlug from categoryName
  const extractSlugs = (categoryName) => {
    if (typeof categoryName !== "string")
      return { categorySlug: "", areaSlug: "" };

    const parts = categoryName.split("-in-");
    const categorySlug = parts[0] || "";

    const areaSlug = parts[1] || "";

    return { categorySlug, areaSlug };
  };

  const { categorySlug, areaSlug } = extractSlugs(categoryName);

  useEffect(() => {
    const fetchData = async () => {
      if (!categorySlug) return;

      const apiUrl = `${getCategoryName}?categorySlug=${categorySlug}&page=${currentPage}`;

      try {
        const response = await axios.post(apiUrl, { lan: hostname, areaSlug });

        if (!response?.data) {
          router.push("/404");
          return;
        }

        const {
          data,
          categoryNamePlural,
          businessCount,
          businessNames,
          areaName,
          categoryNameNew,
          totalPages,
        } = response.data;

        if (!data || data.length === 0 || response.data.error) {
          router.push("/404");
          return;
        }

        setCategoryData({
          data,
          categoryNamePlural,
          businessCount,
          businessNames,
          areaName,
          categoryNameNew,
        });
        setTotalPages(totalPages);
      } catch (error) {
        if (error.response?.status === 404) {
          router.push("/404");
        } else {
          console.error("category name page Error fetching data:", error);
        }
      }
    };

    fetchData();
  }, [categorySlug, currentPage, hostname, areaSlug, router]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setShowPreviousButton(true);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (page) => {
    const pageUrl = `${url}?p=${page}`;
    router.push(pageUrl);
    setCurrentPage(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 3;

    let startPage = Math.max(1, currentPage - 1); // Start showing from one page before the current page
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageClick(1)}
          className={`px-4 py-2 border ${
            currentPage === 1
              ? "bg-blue-500 text-white"
              : "bg-white text-blue-500"
          }`}
        >
          1
        </button>
      );

      if (startPage > 2) {
        pages.push(
          <span key="dots1" className="px-4 py-2">
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageClick(i)}
          className={`px-4 py-2 border ${
            currentPage === i
              ? "bg-blue-500 text-white"
              : "bg-white text-blue-500"
          }`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="dots2" className="px-4 py-2">
            ...
          </span>
        );
      }

      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageClick(totalPages)}
          className={`px-4 py-2 border ${
            currentPage === totalPages
              ? "bg-blue-500 text-white"
              : "bg-white text-blue-500"
          }`}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  const [imageSrc, setImageSrc] = useState("");

  useEffect(() => {
    const categoryImage = categoryData.categoryNameNew
      ? `/assets/img/categories/${categoryData.categoryNameNew
          .split(" ")
          .join("-")
          .replace(/^./, categoryData.categoryNameNew[0].toLowerCase())}.webp`
      : "/assets/img/categories/default.webp";

    setImageSrc(categoryImage);
  }, [categoryData.categoryNameNew]);

  const handleImageError = () => {
    setImageSrc("/assets/img/categories/default.webp");
  };

  const obj = {
    "@context": "https://schema.org",
    "@type": "Category",
    name: categoryData.categoryNameNew,
    businessNames: categoryData.businessNames,
    areaName: categoryData.areaName || "",
    data: categoryData.data.map((business, businessIndex) => ({
      position: businessIndex + 1,
      "@type": "Businesses",
      name: business.businessName,
      url: business.businessSlug,
      address: business.address,
      // Add other business properties as needed
    })),
  };

  return (
    <Layout
      obj={fullObj}
      hostname={hostnameNew}
      countryName={countryName}
      pageTitle={pageTitle}
      countryTranslations={translations}
      metaDescription={metaDescription}
      metaKeywords={metaKeywords}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }}
        />
      </Head>
      <div className="p-5 md:p-10">
        <div className="relative">
          <Image
            src={imageSrc}
            className="w-full h-[225px] object-cover"
            width={1000}
            height={1000}
            alt="image"
            onError={handleImageError}
          />
        </div>
        <div className="flex items-center justify-center my-5 pb-16">
          <div className="w-full md:w-[50%] p-4 text-center bg-white border border-gray-200 rounded-lg shadow sm:p-8 dark:bg-gray-800 dark:border-gray-700 absolute flex items-center justify-center flex-col md:flex-row">
            <div>
              <Image
                src="/assets/images/placeholder.png"
                className="h-12 w-12"
                height={900}
                width={900}
                alt="image"
              />
            </div>
            <div>
              <p className="text-2xl md:text-lg">
                {hostname == "hi" ? (
                  <b>
                    {countryTranslations?.[0]}{" "}
                    {localizedStrings &&
                      localizedStrings.Index.categoryFirstLine4}{" "}
                    {localizedStrings && localizedStrings.Index.Category.name}{" "}
                    {categoryData.data.length === 0 ? (
                      <CardSkeleton />
                    ) : (
                      categoryData.categoryNamePlural.charAt(0).toUpperCase() +
                      categoryData.categoryNamePlural.slice(1)
                    )}
                  </b>
                ) : (
                  <b>
                    {localizedStrings && localizedStrings.Index.Category.name}{" "}
                    {categoryData.data.length === 0 ? (
                      <CardSkeleton />
                    ) : (
                      categoryData.categoryNamePlural.charAt(0).toUpperCase() +
                      categoryData.categoryNamePlural.slice(1)
                    )}{" "}
                    {localizedStrings &&
                      localizedStrings.Index.categoryFirstLine4}{" "}
                    {countryTranslations?.[0]}
                  </b>
                )}
              </p>
            </div>
          </div>
        </div>
        {hostname ? (
          <div className="pb-8">
            <p className="text-lg text-justify">
              {translations?.[1]}{" "}
              {localizedStrings &&
                localizedStrings.Index.Category.categoryFirstLine}{" "}
              {categoryData.areaName} {translations?.[0]}{" "}
              {localizedStrings && localizedStrings.Index.categoryFirstLine4}{" "}
              {localizedStrings &&
                localizedStrings.Index.Category.categoryFirstLine2}{" "}
              {categoryData.businessCount}{" "}
              {categoryData.categoryNamePlural.charAt(0).toUpperCase() +
                categoryData.categoryNamePlural.slice(1)}{" "}
              {localizedStrings &&
                localizedStrings.Index.Category.categoryFirstLine1}{" "}
              {categoryData.areaName} {translations?.[0]}{" "}
              {localizedStrings && localizedStrings.Index.areaExploreBtn1}{" "}
              {localizedStrings &&
                localizedStrings.Index.Category.categorySecondLine1}{" "}
              {categoryData.categoryNamePlural.charAt(0).toUpperCase() +
                categoryData.categoryNamePlural.slice(1)}{" "}
              {localizedStrings && localizedStrings.Index.areaSecondLine2}-{" "}
              {categoryData.businessNames.join(", ")} |
            </p>
          </div>
        ) : (
          <div className="pb-8">
            <p className="text-lg text-justify">
              {translations?.[1]}{" "}
              {localizedStrings &&
                localizedStrings.Index.Category.categoryFirstLine1}{" "}
              {localizedStrings &&
                localizedStrings.Index.Category.categoryFirstLine2}{" "}
              {categoryData.businessCount}{" "}
              {categoryData.categoryNamePlural.charAt(0).toUpperCase() +
                categoryData.categoryNamePlural.slice(1)}{" "}
              {localizedStrings && localizedStrings.Index.categoryFirstLine4}{" "}
              {categoryData.areaName} {translations?.[0]}.{" "}
              {localizedStrings &&
                localizedStrings.Index.Category.categorySecondLine1}{" "}
              {categoryData.categoryNamePlural.charAt(0).toUpperCase() +
                categoryData.categoryNamePlural.slice(1)}{" "}
              {localizedStrings && localizedStrings.Index.categoryFirstLine4}{" "}
              {categoryData.areaName} {translations?.[0]}{" "}
              {localizedStrings && localizedStrings.Index.areaSecondLine2}-{" "}
              {categoryData.businessNames.join(", ")}.
            </p>
          </div>
        )}
        {categoryData.data.length === 0 ? (
          <div>
            <SkeletonTest height="h-[560px]" />
          </div>
        ) : (
          <div className="pb-5">
            <table className="w-full text-sm text-left rtl:text-right ">
              <thead className="text-base text-white bg-blue-500">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 hidden md:table-cell  md:text-base text-3xl"
                  >
                    {localizedStrings &&
                      localizedStrings.Index.Category.placeName}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 md:hidden table-cell text-base"
                  >
                    {categoryData &&
                      categoryData.data.length > 0 &&
                      categoryData.data[0].category}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 hidden md:table-cell  md:text-base text-3xl"
                  >
                    {localizedStrings && localizedStrings.Index.Category.type}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 hidden md:table-cell  md:text-base text-3xl"
                  >
                    {localizedStrings &&
                      localizedStrings.Index.Category.address}
                  </th>
                </tr>
              </thead>
              <tbody>
                {categoryData.data.length > 0 &&
                  categoryData.data.map((c, index) => (
                    <tr
                      key={index}
                      className={`flex flex-col md:table-row even:bg-[#ECECEC] odd:bg-white p-1 pt-4`}
                    >
                      <td className="">
                        <Link
                          href={`/places/${c.businessSlug}`}
                          className="text-blue-400 md:text-base text-lg"
                        >
                          <b>{c.businessName}</b>
                        </Link>
                      </td>
                      <td className="p-0 md:px-6 md:py-4 ">
                        <p className="md:text-base text-lg text-[#666]">
                          {c.category}
                        </p>
                      </td>
                      <td>
                        <div className="flex items-center text-base">
                          <div className="pr-1">
                            <IoLocationSharp className="" />
                          </div>
                          <div>
                            <p className=" md:text-base text-lg">{c.address}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-center items-center">
          {showPreviousButton && (
            <button
              type="button"
              onClick={handlePreviousPage}
              className="text-white bg-blue-800 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800 flex items-center"
            >
              <span className="mx-2">
                <FaLongArrowAltLeft />
              </span>
              {localizedStrings && localizedStrings.Index.Category.btn1}
            </button>
          )}

          <div className="flex space-x-2 mr-2">{renderPageNumbers()}</div>
          {totalPages > 1 && (
            <button
              type="button"
              onClick={handleNextPage}
              className="text-white bg-blue-800 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800 flex items-center"
            >
              {localizedStrings && localizedStrings.Index.Category.btn2}
              <span className="ml-2">
                <FaLongArrowAltRight />
              </span>
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}

export async function getServerSideProps(context) {
  const { req, resolvedUrl, query } = context;
  const { categoryName } = context.query;
  const pro = req.headers["x-forward-proto"] || "https";
  const h = req.headers["host"];
  const full = `${pro}://${h}${resolvedUrl}`;
  const getHttp = process.env.BASE_URL;
  const currentUrl = `${getHttp}://${context.req.headers.host}${context.req.url}`;
  const hostname = hostNameFunction(context.req);

  // Get base URL by removing subdomains
  let baseUrl = currentUrl.split(".com")[0] + ".com"; // Extract base URL before `.com`

  // Extract what comes after `https://`
  let domainPart = baseUrl.replace(/^https?:\/\//, "");

  // Check for `hostname.` or `hostname-` at the start of the domainPart
  if (domainPart.startsWith(`${hostname}.`)) {
    // Replace language subdomain with "www."
    baseUrl = baseUrl.replace(
      new RegExp(`^https?://${hostname}[.]`),
      "https://www."
    );
  } else if (domainPart.startsWith(`${hostname}-`)) {
    // Remove language prefix and hyphen
    baseUrl = baseUrl.replace(new RegExp(`^https?://${hostname}-`), "https://");
  }

  // Determine the country from the base URL or default to 'Unknown Country'
  const country = COUNTRY_NAME[baseUrl] || "Unknown Country";

  // Get the language or default to 'en'
  const language = hostname.split(".")[0] || "en"; // Default to 'en' if no subdomain or 'www'

  // Fetch the translations for the determined country and language
  const countryTranslations =
    translations[country]?.[language] || translations[country]?.["en"] || {};

  const extractSlugs = (categoryName) => {
    if (typeof categoryName !== "string")
      return { categorySlug: "", areaSlug: "" };

    const parts = categoryName.split("-in-");
    const categorySlug = parts[0] || "";
    const areaSlug = parts[1] || "";

    return { categorySlug, areaSlug };
  };

  const { categorySlug, areaSlug } = extractSlugs(categoryName);

  const currentPage = query.p || 1;
  const localizedStrings = await getLocals(hostname);
  const value = `${getCategoryName}?categorySlug=${categorySlug}&page=${currentPage}`;
  const res = await fetch(value, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      lan: hostname,
      areaSlug,
    }), // Pass language dynamically as needed
  });

  const data = await res.json();
  return {
    props: {
      fullObj: full,
      hostnameNew: hostname,
      localizedVariable: localizedStrings,
      translations: countryTranslations,
      data: data,
    },
  };
}
